import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { useEffect } from 'react';
import _ from 'lodash';

import { useStorage } from '~Components/Storage';
import { useSelector } from '~Hooks';
import { useLogger } from '~Hooks/Entities/useLogger';
import { datadogLogs } from '@datadog/browser-logs';

import {
  GetVenueMetadata,
  GetVenueMetadataVariables,
  UpdateMetadataMutation,
  UpdateMetadataMutationVariables,
} from './__queries__';

/**
 * Bag of metadata exposed to the rest of the app. Today this only holds
 * venue-level fields, but it can grow to include other metadata sources.
 *
 * When adding a field:
 *  1. Add it to this interface
 *  2. Add a default value to DEFAULT_METADATA
 *  3. Add it to the GET_VENUE_METADATA query (if venue-scoped)
 *  4. Map it in the effect that refreshes the cache
 */
export interface Metadata {
  venueName: string;
  useCoursesInKDS: boolean;
  staffIdleTimeout: number | null;
  organizationId: string;
  legalName: string;
  zip: string;
  street: string;
  city: string;
}

/**
 * Hardcoded fallbacks used until the GraphQL query resolves and writes the
 * real values into storage. Mirrors the DEFAULT_DATA pattern in Storage so
 * consumers never have to null-check metadata fields.
 */
export const DEFAULT_METADATA: Metadata = {
  venueName: '',
  useCoursesInKDS: false,
  staffIdleTimeout: null,
  organizationId: '',
  legalName: '',
  zip: '',
  street: '',
  city: '',
};

const GET_VENUE_METADATA = gql`
  query GetVenueMetadata($venueId: ID) {
    Venue(id: $venueId) {
      id
      name
      useCoursesInKDS
      staffIdleTimeout

      organizationId
      legalName
      zip
      location {
        street
        city
        zip
      }
    }
  }
`;

const UPDATE_VENUE_METADATA = gql`
  mutation UpdateMetadataMutation($input: UpdateVenueMetadataInput!) {
    updateVenueMetadata(input: $input) {
      success
      error
      message
    }
  }
`;

/**
 * Reads venue metadata, exposing the cached value from storage immediately
 * (so consumers don't have to wait on a query at boot) and refreshing it
 * in the background. Updates go through the metadata service and are
 * optimistically reflected in the cached value.
 */
export const useMetadata = () => {
  const venueId = useSelector((state) => state.venueId!);
  const [storage, commitToStorage] = useStorage();
  const logger = useLogger('use-metadata');

  const venueQuery = useQuery<GetVenueMetadata, GetVenueMetadataVariables>(GET_VENUE_METADATA, {
    variables: { venueId },
    skip: !venueId,
  });

  // Refresh the storage cache when a fresh value comes in from the server.
  useEffect(() => {
    const venue = venueQuery.data?.Venue;
    datadogLogs.addLoggerGlobalContext('venue', venue?.name || DEFAULT_METADATA.venueName);

    if (!venue) {
      return;
    }

    const zipForVenue = venue?.zip || venue?.location?.zip?.toString();

    const fresh: Metadata = {
      venueName: venue.name || DEFAULT_METADATA.venueName,
      useCoursesInKDS: venue.useCoursesInKDS ?? DEFAULT_METADATA.useCoursesInKDS,
      staffIdleTimeout: venue.staffIdleTimeout ?? DEFAULT_METADATA.staffIdleTimeout,
      organizationId: venue.organizationId || DEFAULT_METADATA.organizationId,
      legalName: venue.legalName || DEFAULT_METADATA.legalName,
      zip: zipForVenue || DEFAULT_METADATA.zip,
      street: venue.location.street || DEFAULT_METADATA.street,
      city: venue.location.city || DEFAULT_METADATA.city,
    };

    if (!_.isEqual(fresh, storage.metadata)) {
      logger.info('Refreshing cached metadata from backend', { fields: Object.keys(fresh) });
      commitToStorage({ metadata: fresh });
    }
  }, [venueQuery.data]);

  const [updateVenueMetadataMutation] = useMutation<
    UpdateMetadataMutation,
    UpdateMetadataMutationVariables
  >(UPDATE_VENUE_METADATA);

  const update = async (input: Partial<Metadata>) => {
    if (!venueId) {
      logger.error('Cannot update metadata without a venueId', { fields: Object.keys(input) });
      return;
    }

    // Optimistically update the cache so the UI reacts instantly.
    commitToStorage({ metadata: { ...storage.metadata, ...input } });

    try {
      const result = await updateVenueMetadataMutation({
        variables: { input: { venueId, ...input } },
      });

      if (!result.data?.updateVenueMetadata.success) {
        logger.error('Update venue metadata returned failure', {
          errorMessage: result.data?.updateVenueMetadata.error,
          fields: Object.keys(input),
        });
      } else {
        logger.info('Updated venue metadata', { fields: Object.keys(input) });
      }
    } catch (err) {
      logger.error('Failed to update venue metadata', {
        errorMessage: (err as Error).message,
        fields: Object.keys(input),
      });
    }
  };

  return {
    data: storage.metadata,
    update,
    loading: venueQuery.loading,
  };
};
